import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { 
  Users,
  UserCheck,
  UserX,
  Clock,
  Mail,
  MapPin,
  FileText,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending_verification: { label: 'Pending Verification', color: 'bg-gray-100 text-gray-800' },
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
};

const UserCard = ({ user, onApprove, onReject }) => {
  const [loading, setLoading] = useState(false);
  const config = statusConfig[user.status] || statusConfig.pending_approval;
  
  const handleApprove = async () => {
    setLoading(true);
    await onApprove(user.id);
    setLoading(false);
  };
  
  const handleReject = async () => {
    const reason = prompt('Rejection reason (optional):');
    setLoading(true);
    await onReject(user.id, reason || '');
    setLoading(false);
  };
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#07743F] rounded-full flex items-center justify-center text-white text-lg font-bold">
              {user.name?.charAt(0) || 'U'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{user.name}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                <Mail className="w-3 h-3" />
                {user.email}
              </div>
              {user.country && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                  <MapPin className="w-3 h-3" />
                  {user.country}
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                <FileText className="w-3 h-3" />
                {user.story_count || 0} stories
              </div>
              <div className="text-xs text-gray-400 mt-2">
                Registered: {new Date(user.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={config.color}>{config.label}</Badge>
            {user.email_verified && (
              <Badge variant="outline" className="text-green-600 border-green-300">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Email Verified
              </Badge>
            )}
          </div>
        </div>
        
        {user.status === 'pending_approval' && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            <Button
              onClick={handleApprove}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white flex-1"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
            <Button
              onClick={handleReject}
              disabled={loading}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 flex-1"
            >
              <UserX className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </div>
        )}
        
        {user.status === 'rejected' && user.rejection_reason && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-red-600">
              <strong>Rejection reason:</strong> {user.rejection_reason}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const UsersPage = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        setPendingCount(data.filter(u => u.status === 'pending_approval').length);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to approve user:', error);
    }
  };

  const handleReject = async (userId, reason) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to reject user:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true;
    if (filter === 'pending') return user.status === 'pending_approval';
    if (filter === 'approved') return user.status === 'approved';
    if (filter === 'contributors') return user.role === 'contributor';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-sm text-gray-600">
                Manage contributors and approve registrations
              </p>
            </div>
            <div className="flex items-center gap-4">
              {pendingCount > 0 && (
                <Badge className="bg-yellow-100 text-yellow-800 px-4 py-2">
                  <Clock className="w-4 h-4 mr-2" />
                  {pendingCount} pending approval
                </Badge>
              )}
              <Button
                onClick={fetchUsers}
                variant="outline"
                className="border-[#07743F] text-[#07743F]"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'all', label: 'All Users' },
            { id: 'pending', label: 'Pending Approval' },
            { id: 'approved', label: 'Approved' },
            { id: 'contributors', label: 'Contributors Only' },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={filter === tab.id ? 'default' : 'outline'}
              onClick={() => setFilter(tab.id)}
              className={filter === tab.id ? 'bg-[#07743F] text-white' : ''}
            >
              {tab.label}
              {tab.id === 'pending' && pendingCount > 0 && (
                <Badge className="ml-2 bg-yellow-500 text-white">{pendingCount}</Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                <p className="text-xs text-gray-600">Total Users</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
                <p className="text-xs text-gray-600">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.status === 'approved').length}
                </p>
                <p className="text-xs text-gray-600">Approved</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'contributor').length}
                </p>
                <p className="text-xs text-gray-600">Contributors</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User List */}
        {filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No users found</h3>
              <p className="text-gray-600">No users match the selected filter.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredUsers.map(user => (
              <UserCard
                key={user.id}
                user={user}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
